import { animated } from '@react-spring/web'
import PropTypes from 'prop-types'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'

import useFormatMessage from '@hooks/useFormatMessage'
import { useFadeAnimation, useReducedMotion } from '@utils/AnimationsHelper'
import AnimatedIcon from '@components/AnimatedIcon'
import BackgroundCircles from '@components/BackgroundCircles'
import { styles as dialogStyles } from '@components/Dialog/Dialog.utils'

LeaveDialog.propTypes = {
  /** Whether the dialog is shown */
  open: PropTypes.bool.isRequired,

  /** Guard source; copy comes from `ui.leavePrompt.<source>.*` */
  source: PropTypes.string.isRequired,

  /** MUI icon component for the hero */
  icon: PropTypes.elementType.isRequired,

  /** Called when the user chooses to stay, closes the backdrop or presses escape */
  onStay: PropTypes.func.isRequired,

  /** Called when the user chooses to leave */
  onLeave: PropTypes.func.isRequired,

  /** Called once the close transition has finished */
  onExited: PropTypes.func,

  /** Shows progress on the leave action while it runs */
  loading: PropTypes.bool,
}

const testId = 'leave-prompt-dialog'

/**
 * The "leave?" dialog. Leaving is the quiet outlined action; staying is the filled accent
 * action and takes focus, since it is the one we want. Shares the hero and entrance of the
 * empty-state pages so the interruption reads as part of the app rather than a system alert.
 */
export default function LeaveDialog({ open, source, icon, onStay, onLeave, onExited, loading = false }) {
  const formatMessage = useFormatMessage()
  const reducedMotion = useReducedMotion()
  const textSpring = useFadeAnimation({ delay: 150, immediate: reducedMotion })
  const actionsSpring = useFadeAnimation({ delay: 300, immediate: reducedMotion })

  return (
    <Dialog
      open={open}
      onClose={onStay}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: { 'data-testid': testId, sx: [dialogStyles.paper, { overflow: 'hidden' }] },
        transition: { onExited },
      }}
      aria-labelledby={`${testId}-title`}
      aria-describedby={`${testId}-description`}
    >
      <BackgroundCircles scale={0.75} />

      <Box sx={styles.hero}>
        <AnimatedIcon icon={icon} size={96} />
      </Box>

      <Box component={animated.div} style={textSpring}>
        <DialogTitle id={`${testId}-title`} variant="h5" sx={styles.title}>
          {formatMessage(`ui.leavePrompt.${source}.title`)}
        </DialogTitle>
        <DialogContent sx={styles.content}>
          <Typography id={`${testId}-description`} sx={styles.description}>
            {formatMessage(`ui.leavePrompt.${source}.message`)}
          </Typography>
        </DialogContent>
      </Box>

      <DialogActions component={animated.div} style={actionsSpring} sx={styles.actions}>
        <Button {...dialogStyles.button} variant="outlined" onClick={onLeave} loading={loading} data-testid={`${testId}-leave`}>
          {formatMessage(`ui.leavePrompt.${source}.leave`)}
        </Button>
        <Button {...dialogStyles.button} autoFocus variant="contained" color="secondary" onClick={onStay} data-testid={`${testId}-stay`}>
          {formatMessage(`ui.leavePrompt.${source}.stay`)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const styles = {
  hero: {
    display: 'flex',
    justifyContent: 'center',
    pt: 5,
    pb: 3,
  },
  title: {
    fontWeight: 'bold',
    pt: 0,
    pb: 1,
    px: 3,
  },
  content: {
    px: 3,
    pb: 3,
  },
  description: {
    color: 'text.secondary',
    maxWidth: 320,
    mx: 'auto',
    textWrap: 'pretty',
  },
  actions: {
    ...dialogStyles.actions,
    px: 3,
    pb: 3,
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    '& > :not(:first-of-type)': { ml: 0 },
  },
}
